'use client'

import {
  ErrorBoundary,
  type ErrorBoundaryProps,
  type FallbackProps,
} from 'react-error-boundary'

interface LoggedErrorBoundaryBaseProps {
  children: React.ReactNode
  onError?: ErrorBoundaryProps['onError']
  logLabel?: string
  /**
   * Extra fields to log alongside the error; a thunk defers building them to
   * the failure path. `object` rather than a record type because callers hand
   * us whatever shape their own logs need — including Typesense hits typed
   * with `unknown` values.
   */
  logContext?: object | (() => object)
}

type LoggedErrorBoundaryProps = LoggedErrorBoundaryBaseProps &
  (
    | {
        fallback: React.ReactNode
        fallbackRender?: never
      }
    | {
        fallback?: never
        fallbackRender: (props: FallbackProps) => React.ReactNode
      }
  )

export function LoggedErrorBoundary({
  children,
  fallback,
  fallbackRender,
  onError,
  logLabel = '[LoggedErrorBoundary] Render failed',
  logContext,
}: LoggedErrorBoundaryProps) {
  const fallbackProps = fallbackRender ? { fallbackRender } : { fallback }

  return (
    <ErrorBoundary
      {...fallbackProps}
      onError={(error, info) => {
        console.error(logLabel, error, {
          componentStack: info.componentStack,
          context: logContext instanceof Function ? logContext() : logContext,
        })

        onError?.(error, info)
      }}
    >
      {children}
    </ErrorBoundary>
  )
}
